import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { share } from 'rxjs/operators';
import { IPopDynamicEvent } from './pop-dynamic-event';
import { PopParentElement } from '@components/pop/pop-parent-element';
import { isDefined } from '@core/utils/object';

@Injectable()
export class PopService {
    private activeComponents: any;
    private observable$: Subject<IPopDynamicEvent>;
    private refresher$: Subject<any>;

    constructor() {
        this.activeComponents = {};
        this.observable$ = new Subject();
        this.refresher$ = new Subject();
    }

    registerPopover(id: string, instance: string) {
        if (!isDefined(this.activeComponents[id]) || this.activeComponents[id] === null) {
            this.activeComponents[id] = {};
        }

        if (!isDefined(this.activeComponents[id][instance]) || this.activeComponents[id][instance] === null) {
            this.activeComponents[id][instance] = {
                show: false
            };
        }

        return this.observable$
          .pipe(
            share()
          );
    }

    getRefresher() {
        return this.refresher$
          .pipe(
            share()
          );
    }

    refresh(id: string, instance: string) {
        this.refresher$.next({id, instance});
    }

    unregisterPopover(id: string, instance: string) {
        if (isDefined(this.activeComponents[id]) && this.activeComponents[id] !== null &&
          isDefined(this.activeComponents[id][instance]) && this.activeComponents[id][instance] !== null) {
            delete this.activeComponents[id][instance];
        }
    }

    isRegistered(id: string, instance: string): boolean {
        if (!isDefined(this.activeComponents[id]) || this.activeComponents[id] === null) {
            return false;
        } else if (!isDefined(this.activeComponents[id][instance]) || this.activeComponents[id][instance] === null) {
            return false;
        } else {
            return true;
        }
    }

    isPopoverOpen(id: string, instance: string): boolean {
        if (!isDefined(this.activeComponents[id]) || this.activeComponents[id] === null) {
            return false;
        } else if (!isDefined(this.activeComponents[id][instance]) || this.activeComponents[id][instance] === null) {
            return false;
        } else {
            return this.activeComponents[id][instance].show;
        }
    }

    test(id: string, instance: string) {
        this.observable$.next({id, instance});
    }

    open(id: string, instance: string, target: HTMLElement|PopParentElement, context: any) {
        this.observable$.next({
            id,
            instance,
            show: true,
            force: false,
            element: target,
            context
        });

        if (this.activeComponents[id][instance]) {
            this.activeComponents[id][instance].show = true;
        }
    }

    /**
     * This is to explicity set the status of the instance. Mainly used by `Popovercomponent` since
     * it does not use the service to do the other operations. The Popovercomponent sets the status
     * whenever the `show` value changes keeping this in sync
     *
     * @param {string} id
     * @param {string} instance
     * @param {boolean} show
     */
    setStatus(id: string, instance: string, show: boolean) {
        if (this.activeComponents && this.activeComponents[id] && this.activeComponents[id][instance]) {
            this.activeComponents[id][instance].show = show;
        }
    }

    close(id: string, instance: string, force?: boolean) {
        this.observable$.next({id, instance, show: false, force});
        
        if (this.activeComponents[id][instance]) {
            this.activeComponents[id][instance].show = false;
        }
    }

    /**
     * Close all popovers.
     */
    closeAll() {
        Object.keys(this.activeComponents).forEach((id) => {
            const componentInstances = this.activeComponents[id];
            Object.keys(componentInstances).forEach((key) => {
                this.observable$.next({id, instance: key, show: false, force: true});

                if (componentInstances[key]) {
                    componentInstances[key].show = false;
                }
            });
        });
    }
}
